import type React from 'react';
import { useEffect, useState } from 'react';

import * as switchStyles from '@/switchElement/Switch.css';
import { Switch, SwitchThumb } from '@/ui/Switch';
import { cn } from '@/utils/cn';

import type { TrueFalseElementProps } from './TrueFalse.types';

export function TrueFalse(props: TrueFalseElementProps) {
  const { element, onChange, isLive, preferenceId, translations } = props;
  const { data } = element.attrs || {};

  // Internal state for optimistic UI feedback
  const [internalValue, setInternalValue] = useState(() => !!data?.value);

  // Keep internal state in sync if value prop changes
  useEffect(() => {
    setInternalValue(!!data?.value);
  }, [data?.value]);

  if (!data || !element.attrs) {
    return null;
  }

  const { description, required } = data;

  const handleChange = (newValue: boolean) => {
    setInternalValue(newValue);
    if (data.type === 'publication') {
      onChange(newValue, data.publication_id);
    } else if ((data.type === 'custom_field' || data.type === 'newsletter_list') && preferenceId) {
      onChange(newValue, preferenceId);
    }
  };

  const getLabelText = () => {
    if (data.type === 'publication') {
      if (internalValue) {
        return `Opt out of receiving ${data.publication_name}`;
      }
      return `Opt in to receiving ${data.publication_name}`;
    }
    return data.name;
  };

  const getId = () => {
    if (data.type === 'publication') {
      return data.publication_id;
    }
    if (data.type === 'newsletter_list') {
      return data.list_id;
    }
    return data.custom_field_id;
  };

  const id = `true-false-${data.type}-${getId()}`;

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="cursor-pointer block font-medium text-sm text-gray-900">
          {getLabelText()}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {description && <p className="text-xs text-gray-700 font-normal">{description}</p>}
      </div>
      <div className="flex flex-row gap-3 items-center">
        {data.type === 'publication' && (
          <span className="text-sm font-medium text-gray-700">{translations?.['manage.preferences.subscribed']}</span>
        )}
        <Switch
          id={id}
          checked={internalValue}
          onCheckedChange={handleChange}
          className={cn(switchStyles.switchRoot, 'shadow-sm', !isLive && 'pointer-events-none')}
          style={
            {
              '--background-color': !internalValue ? '#E5E7EB' : 'var(--pt-background-color)',
              '--thumb-color': '#ffffff',
            } as React.CSSProperties
          }
        >
          <SwitchThumb className={switchStyles.switchThumb} />
        </Switch>
      </div>
    </div>
  );
}
